import { MessageSquareMore, QrCode, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useDDSMonthlyThemes } from '@/api/queries/dds-monthly-themes-queries'
import { useCreateDDS } from '@/api/queries/dds-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { usePeople } from '@/api/queries/people-queries'
import { QRCodeButton } from '@/components/commons/qr-code-button'
import { DdsDurationPicker } from '@/components/dds-attendance'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardFooter,
	InspectionCardHeader,
	InspectionDateField,
	InspectionOptionGroup,
	InspectionPhotoField,
} from '@/components/inspections'
import { Button } from '@/components/ui/button'
import { CardContent } from '@/components/ui/card'
import { FieldContent } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { ROUTES } from '@/constants/routes'
import { useDDSForm } from '@/hooks/forms/use-dds-form'
import { useFormErrorFocus } from '@/hooks/forms/use-form-error-focus'
import { useDdsBadgeScan } from '@/hooks/use-dds-badge-scan'
import type { DdsFormData } from '@/schemas/dds-schema'
import { useProfileStore } from '@/store'
import type { BadgeScanMode, DDSType } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'
import { formatDateYMD } from '@/utils/formatDate'
import { convertHeicIfNeeded } from '@/utils/image-compression'

export const DDSPage = () => {
	const navigate = useNavigate()
	const { data: personData, isLoading } = usePeople(true)
	const { resolveScannedPerson } = useDdsBadgeScan(personData)
	const { t } = useTranslation()
	const form = useDDSForm()
	const {
		setFocus,
		formState: { errors },
	} = form
	const { handleError } = useFormErrorFocus(errors, setFocus)

	const createMutation = useCreateDDS()

	const colors = INSPECTION_COLORS.honeyYellow

	const { defaultBranchId, defaultContractId, defaultCustomerId } =
		useProfileStore()
	const { data: branchOptions } = useOrganization()

	// Context fields (persisted in zustand)
	const branchId = defaultBranchId
	const crId = defaultContractId
	const [ddsDate, setDdsDate] = useState<Date | undefined>(new Date())

	const selectedBranch = branchOptions?.find((b) => b.id === branchId)
	const branchName = selectedBranch?.name || ''

	const personIds = form.watch('attendeeIds')
	const image = form.watch('image')
	const selectedType = form.watch('type')
	const attendanceEnabled = form.watch('attendanceEnabled')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [photoPreview, setPhotoPreview] = useState<string | null>(null)

	// ── Monthly theme auto-fill (DIG/DMI only) ──
	const isMonthlyThemeType = selectedType === 'DIG' || selectedType === 'DMI'
	const currentMonth = new Date().getMonth() + 1

	const { data: monthlyThemeData } = useDDSMonthlyThemes(
		{ month: currentMonth, type: selectedType as 'DIG' | 'DMI' },
		{ enabled: isMonthlyThemeType },
	)

	useEffect(() => {
		if (!isMonthlyThemeType) return

		const themeDescription = monthlyThemeData?.items?.[0]?.description
		if (!themeDescription) return

		form.setValue('subject', themeDescription, {
			shouldDirty: true,
			shouldValidate: true,
		})
	}, [isMonthlyThemeType, monthlyThemeData, form])

	const typeOptions = [
		{ value: 'DDS', label: t('dds.typeDDS') },
		{ value: 'PIT_STOP', label: t('dds.typePitStop') },
		{ value: 'DIG', label: t('dds.typeDig') },
		{ value: 'DMI', label: t('dds.typeDMI') },
		{
			value: 'DDS_GERAL',
			label: t('dds.typeDdsGeral'),
			shortLabel: t('dds.typeDdsGeralShort'),
		},
	]

	const handleCancel = () => {
		navigate(ROUTES.DDS.INDEX)
	}

	const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0]
		if (!picked) return
		const file = await convertHeicIfNeeded(picked)
		form.setValue('image', file, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setPhotoPreview(URL.createObjectURL(file))
	}

	const handleRemovePhoto = () => {
		form.setValue('image', null, {
			shouldDirty: true,
			shouldValidate: true,
		})
		if (photoPreview) URL.revokeObjectURL(photoPreview)
		setPhotoPreview(null)
	}

	const handleRemoveBadge = (id: string) => {
		form.setValue(
			'attendeeIds',
			personIds.filter((personId) => personId !== id),
			{
				shouldDirty: true,
				shouldValidate: true,
			},
		)
	}

	const handleSubmit = async (formData: DdsFormData) => {
		const selectedCr = selectedBranch?.crs?.find((c) => c.id === crId)

		// require customer when CR has customers
		if ((selectedCr?.customers?.length ?? 0) > 0 && !defaultCustomerId) {
			toast.error(
				t('inspections.common.selectCustomerRequired') ||
					'Selecione um cliente',
			)
			return
		}

		if (!crId || !ddsDate) {
			toast.error(t('dds.validationError'))
			return
		}

		setIsSubmitting(true)

		try {
			const created = await createMutation.mutateAsync({
				crId,
				...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				branchId: defaultBranchId,
				date: formatDateYMD(ddsDate),
				type: formData.type as DDSType,
				subject: formData.subject,
				description: formData.description,
				attendeeIds: formData.attendeeIds,
				durationMinutes: formData.durationMinutes,
				...(formData.attendanceEnabled && { selfCheckIn: true }),
				image: image ?? undefined,
			})

			toast.success(t('dds.saveSuccess'))
			navigate(
				formData.attendanceEnabled
					? ROUTES.DDS.ATTENDANCE.replace(':id', created.id)
					: ROUTES.DDS.INDEX,
			)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleQRCodeScan = async (
		code: string,
		mode: BadgeScanMode,
	): Promise<void> => {
		const scannedBadge = await resolveScannedPerson(code, mode)
		if (!scannedBadge) return

		const currentIds = form.getValues('attendeeIds')
		const alreadyScanned = currentIds.includes(scannedBadge.id)
		if (alreadyScanned) {
			toast.warning(t('dds.badgeAlreadyScanned'))
			return
		}

		form.setValue('attendeeIds', [...currentIds, scannedBadge.id], {
			shouldDirty: true,
			shouldValidate: true,
		})

		if (!alreadyScanned) {
			toast.success(t('dds.badgeScanned', { name: scannedBadge.name }))
		}
	}

	const resetPersonIds = () => {
		form.setValue('attendeeIds', [], {
			shouldDirty: true,
			shouldValidate: true,
		})
	}

	return (
		<InspectionCard colors={colors}>
			<InspectionCardHeader
				icon={<MessageSquareMore className="size-5" />}
				title={t('dds.title')}
				colors={colors}
				onBack={handleCancel}
				branch={branchName}
				branchId={branchId}
				branchList={branchOptions}
				crId={crId}
				crList={selectedBranch?.crs}
			/>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit, handleError)}
					className="flex flex-1 flex-col"
				>
					<CardContent className="flex flex-col gap-6 py-5">
						{/* ── Context fields ── */}
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
							<InspectionDateField
								label={t('inspections.common.date')}
								value={ddsDate}
								onChange={setDdsDate}
								isDisabled={true}
							/>

							<FormField
								name="subject"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('dds.subject')}
										error={form.formState.errors.subject?.message}
									>
										<FieldContent fieldName={field.name}>
											<Input
												id="subject"
												type="text"
												name={field.name}
												ref={field.ref}
												value={field.value}
												onChange={field.onChange}
												placeholder={t('dds.subjectPlaceholder')}
												className="w-full"
											/>
										</FieldContent>
									</FormItem>
								)}
							/>
						</div>

						<Separator className="bg-border/60" />

						<FormField
							name="type"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('dds.type')}
									error={form.formState.errors.type?.message}
								>
									<FieldContent fieldName={field.name}>
										<InspectionOptionGroup
											value={field.value}
											onChange={field.onChange}
											options={typeOptions}
											activeClass={colors.optionActive}
											columns={5}
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="description"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('dds.description')}
									error={form.formState.errors.description?.message}
								>
									<FieldContent fieldName={field.name}>
										<Textarea
											id="description"
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={field.onChange}
											placeholder={t('dds.descriptionPlaceholder')}
											rows={4}
											className="resize-y"
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<Separator className="bg-border/60" />

						{/* Tempo em que o DDS fica aberto para receber participantes */}
						<FormField
							name="durationMinutes"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('form.dds.durationLabel')}
									error={form.formState.errors.durationMinutes?.message}
								>
									<FieldContent fieldName={field.name}>
										<DdsDurationPicker
											onChange={field.onChange}
											value={field.value}
										/>
										<p className="mt-2 text-muted-foreground text-xs">
											{t('form.dds.durationHint')}
										</p>
									</FieldContent>
								</FormItem>
							)}
						/>

						{/* Presença por QR (participantes escaneiam com o próprio celular) */}
						<div className="flex flex-col gap-3 rounded-lg border border-dashed border-kaefer-honey-yellow/60 bg-kaefer-honey-yellow/5 p-3">
							<label
								className="flex cursor-pointer items-start justify-between gap-3"
								htmlFor="dds-attendance-enabled"
							>
								<span className="flex items-start gap-2">
									<QrCode className="mt-0.5 size-4 shrink-0 text-kaefer-pumpkin-orange" />
									<span>
										<span className="block font-medium text-kaefer-dark-grey text-sm">
											{t('form.dds.attendanceListLabel')}
										</span>
										<span className="block text-muted-foreground text-xs">
											{t('form.dds.attendanceListHint')}
										</span>
									</span>
								</span>
								<Switch
									checked={attendanceEnabled}
									id="dds-attendance-enabled"
									onCheckedChange={(checked) =>
										form.setValue('attendanceEnabled', checked, {
											shouldDirty: true,
											shouldValidate: form.formState.isSubmitted,
										})
									}
								/>
							</label>
						</div>

						<FormField
							name="attendeeIds"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('dds.participants')}
									isRequired={!attendanceEnabled}
									error={form.formState.errors.attendeeIds?.message}
								>
									<FieldContent fieldName={field.name}>
										<div className="flex items-center justify-between">
											{personIds.length > 0 && (
												<span className="text-xs text-muted-foreground tabular-nums">
													{personIds.length}{' '}
													{personIds.length === 1 ? 'crachá' : 'crachás'}
												</span>
											)}
										</div>

										{/* Scan Button */}
										<QRCodeButton
											disabled={isLoading}
											handler={handleQRCodeScan}
											resetScan={resetPersonIds}
											scannedCodes={field.value}
											multiscan={true}
										/>

										{personIds.length > 0 && (
											<div className="flex justify-end mt-2">
												<Button
													type="button"
													variant="cancel"
													size="sm"
													onClick={resetPersonIds}
												>
													{t('qrScanner.clearAll')}
												</Button>
											</div>
										)}

										{/* Scanned Badges List */}
										{personIds.length > 0 ? (
											<div className="mt-3 flex flex-col gap-2 overflow-y-auto">
												{personIds.map((personId) => (
													<div
														key={personId}
														className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5"
													>
														<div className="flex-1 min-w-0">
															<p className="text-sm font-medium text-kaefer-dark-grey truncate">
																{personData?.find((p) => p.id === personId)
																	?.name || personId}
															</p>
															<p className="text-xs text-muted-foreground">
																{t('dds.badgeCode')}:{' '}
																{personData?.find((p) => p.id === personId)
																	?.badge || personId}
															</p>
														</div>
														<Button
															type="button"
															variant="ghost"
															size="icon-xs"
															onClick={() => handleRemoveBadge(personId)}
															aria-label={t('dds.removeBadge')}
															className="text-muted-foreground hover:text-destructive"
														>
															<X className="size-3.5" />
														</Button>
													</div>
												))}
											</div>
										) : (
											<p className="mt-3 text-center text-sm text-muted-foreground py-6 border border-dashed border-border rounded-lg">
												{t('dds.noBadges')}
											</p>
										)}
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="image"
							control={form.control}
							render={() => (
								<FormItem label={t('dds.photo')} isRequired={false} error="">
									<InspectionPhotoField
										attachLabel={t('dds.attachPhoto')}
										altText={t('dds.photoSelected')}
										preview={photoPreview}
										onFileChange={handlePhotoChange}
										onRemove={handleRemovePhoto}
										colors={colors}
									/>
								</FormItem>
							)}
						/>
					</CardContent>

					<InspectionCardFooter
						colors={colors}
						isSubmitting={isSubmitting}
						onCancel={handleCancel}
						cancelLabel={t('dds.cancel')}
						saveLabel={t('dds.save')}
						savingLabel={t('dds.saving')}
					/>
				</form>
			</Form>
		</InspectionCard>
	)
}
